// src/admin/pages/reservation/ReservationsManagement.tsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  Clear as ClearIcon,
  Delete as DeleteIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  FilterList as FilterListIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { useAuth } from '../../../contexts/AuthContext';
import { ReservationService, StaffReservationInput } from './reservationService';
import { Reservation, RESERVATION_STATUSES } from '../../../enums/reservation';
import ReservationTable from './ReservationTable';
import ReservationDecisionDrawer, { Decision } from './ReservationDecisionDrawer';
import AddReservationDrawer from './AddReservationDrawer';
import ToastNotification from '../common/Toastnotification';

const RESERVATIONS_PER_PAGE = 10;

interface ToastState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'warning' | 'info';
}

const ReservationsManagement: React.FC = () => {
  const { currentUser, userProfile, hasPermission, isSuperAdmin } = useAuth();
  const userName = userProfile?.displayName || currentUser?.email || 'Admin';

  const canEdit = isSuperAdmin || hasPermission('reservations.edit');
  const canDelete = isSuperAdmin || hasPermission('reservations.delete');
  const canCreate = isSuperAdmin || hasPermission('reservations.create');

  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const tableWrapperRef = useRef<HTMLDivElement>(null);

  // ── Data state ────────────────────────────────────────────────────────────
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [stats, setStats] = useState({ total: 0, newInquiries: 0, reserved: 0, sold: 0 });
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState('');

  // ── UI state ──────────────────────────────────────────────────────────────
  const [deleteTarget, setDeleteTarget] = useState<Reservation | null>(null);
  const [deleteLoading, setDeleteLoading] = useState('');
  const [deleteDialogLoading, setDeleteDialogLoading] = useState(false);
  const [toast, setToast] = useState<ToastState>({ open: false, message: '', severity: 'info' });

  // ── Approve/Reject decision dialog ───────────────────────────────────────
  const [decisionTarget, setDecisionTarget] = useState<Reservation | null>(null);
  const [decisionType, setDecisionType] = useState<Decision | null>(null);

  // ── Add reservation dialog ───────────────────────────────────────────────
  const [addDialogOpen, setAddDialogOpen] = useState(false);

  // ── Search & filter state ────────────────────────────────────────────────
  const [currentPage, setCurrentPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);

  const showToast = (message: string, severity: ToastState['severity']) =>
    setToast({ open: true, message, severity });
  const handleCloseToast = () => setToast((t) => ({ ...t, open: false }));

  const load = useCallback(async () => {
    setLoading(true);
    setErrorMsg('');
    try {
      const [all, statsData] = await Promise.all([
        ReservationService.getAll(),
        ReservationService.getStats(),
      ]);
      setReservations(all);
      setStats(statsData);
    } catch (err: any) {
      setErrorMsg(err.message || 'Failed to fetch reservations');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // ── Filtering (client-side — dataset is bounded to admin-visible leads) ───
  const filtered = useMemo(() => {
    return reservations.filter((r) => {
      const matchesStatus = statusFilter === 'all' || r.status === statusFilter;
      const q = searchQuery.trim().toLowerCase();
      const matchesSearch =
        !q ||
        r.customerName?.toLowerCase().includes(q) ||
        r.customerPhone?.toLowerCase().includes(q) ||
        r.carName?.toLowerCase().includes(q);
      return matchesStatus && matchesSearch;
    });
  }, [reservations, statusFilter, searchQuery]);

  const totalFiltered = filtered.length;
  const totalPages = Math.ceil(totalFiltered / RESERVATIONS_PER_PAGE);
  const pageItems = filtered.slice(
    (currentPage - 1) * RESERVATIONS_PER_PAGE,
    currentPage * RESERVATIONS_PER_PAGE
  );

  const resetToFirstPage = () => setCurrentPage(1);
  const handlePageChange = (_e: React.ChangeEvent<unknown>, page: number) => setCurrentPage(page);

  const handleSearchKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      setSearchQuery(searchInput);
      resetToFirstPage();
    }
  };

  const handleStatusFilterChange = (value: string) => {
    setStatusFilter(value);
    resetToFirstPage();
  };

  const clearFilters = () => {
    setSearchInput('');
    setSearchQuery('');
    setStatusFilter('all');
    resetToFirstPage();
  };

  const hasActiveFilters = searchQuery !== '' || statusFilter !== 'all';
  const activeFiltersCount = [searchQuery !== '', statusFilter !== 'all'].filter(Boolean).length;

  // ── Approve / Reject ──────────────────────────────────────────────────────
  const openApprove = (r: Reservation) => {
    setDecisionTarget(r);
    setDecisionType('Approved');
  };

  const openReject = (r: Reservation) => {
    setDecisionTarget(r);
    setDecisionType('Rejected');
  };

  const closeDecisionDialog = () => {
    setDecisionTarget(null);
    setDecisionType(null);
  };

  const handleDecisionConfirm = async (reason: string) => {
    if (!decisionTarget?.id || !decisionType) return;
    try {
      await ReservationService.update(
        decisionTarget.id,
        { status: decisionType, decisionReason: reason || undefined },
        currentUser?.uid || '',
        userName
      );
      showToast(
        `Reservation ${decisionType.toLowerCase()}${decisionTarget.customerEmail ? ' — customer will be notified by email' : ''}`,
        decisionType === 'Approved' ? 'success' : 'info'
      );
      closeDecisionDialog();
      await load();
    } catch (err: any) {
      showToast(`Failed to ${decisionType.toLowerCase()} reservation: ` + (err.message || 'Unknown error'), 'error');
    }
  };

  // ── Add reservation (staff-entered) ──────────────────────────────────────
  const handleAddReservation = async (data: StaffReservationInput) => {
    await ReservationService.create(data, currentUser?.uid || '', userName);
    setAddDialogOpen(false);
    showToast('Reservation added successfully', 'success');
    await load();
  };

  // ── Delete ────────────────────────────────────────────────────────────────
  const handleDeleteConfirm = async () => {
    if (!deleteTarget?.id) return;
    try {
      setDeleteDialogLoading(true);
      setDeleteLoading(deleteTarget.id);
      await ReservationService.delete(deleteTarget.id, currentUser?.uid || '', userName);
      setDeleteTarget(null);
      showToast('Reservation deleted successfully', 'success');
      resetToFirstPage();
      await load();
    } catch (err: any) {
      showToast('Failed to delete reservation: ' + (err.message || 'Unknown error'), 'error');
    } finally {
      setDeleteDialogLoading(false);
      setDeleteLoading('');
    }
  };

  const handleRefresh = () => {
    load();
    showToast('Reservations refreshed', 'info');
  };

  // ── Initial full-page loading state ─────────────────────────────────────
  if (loading && reservations.length === 0) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minHeight="400px" flexDirection="column">
        <CircularProgress size={40} />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Loading reservations...
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: { md: '100%' },
        display: 'flex',
        flexDirection: 'column',
        overflow: { xs: 'visible', md: 'hidden' },
        p: { xs: 2, sm: 3 },
      }}
    >
      {/* ── Fixed Header ── */}
      <Box sx={{ flexShrink: 0 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          spacing={2}
          sx={{ mb: 3 }}
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <ReceiptLongIcon sx={{ fontSize: { xs: 28, md: 32 }, color: 'primary.main' }} />
            <Box>
              <Typography variant="h4" component="h1" sx={{ fontSize: { xs: '1.5rem', md: '2rem' } }}>
                Reservations
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Every enquiry, test drive request, and reservation submitted from the website
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" spacing={1}>
            <Button onClick={handleRefresh} variant="outlined" startIcon={<RefreshIcon />} disabled={loading}>
              Refresh
            </Button>
            {canCreate && (
              <Button onClick={() => setAddDialogOpen(true)} variant="contained" startIcon={<AddIcon />}>
                Add Test Drive Request
              </Button>
            )}
          </Stack>
        </Stack>

        {/* ── Error banner ── */}
        {errorMsg && (
          <Alert
            severity="error"
            sx={{ mb: 3 }}
            action={
              <Button color="inherit" size="small" onClick={handleRefresh} startIcon={<RefreshIcon />}>
                Retry
              </Button>
            }
          >
            {errorMsg}
          </Alert>
        )}

        {/* ── Stats Cards ── */}
        <Grid container spacing={{ xs: 1, sm: 1.5 }} sx={{ mb: 3 }} alignItems="stretch">
          {[
            { label: 'Total', value: stats.total },
            { label: 'New Inquiries', value: stats.newInquiries },
            { label: 'Reserved (Deposit Paid)', value: stats.reserved },
            { label: 'Sold', value: stats.sold },
          ].map((s) => (
            <Grid item xs={6} sm={6} md={3} key={s.label} sx={{ display: 'flex' }}>
              <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent
                  sx={{
                    p: 1.25,
                    '&:last-child': { pb: 1.25 },
                    flex: 1,
                  }}
                >
                  <Typography color="text.secondary" gutterBottom variant="caption" noWrap sx={{ display: 'block', mb: 0.25 }}>
                    {s.label}
                  </Typography>
                  <Typography
                    variant="h6"
                    component="div"
                    color="text.primary"
                    sx={{ fontSize: { xs: '1rem', sm: '1.15rem' }, fontWeight: 700 }}
                  >
                    {s.value}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* ── Search & Filters ── */}
        <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 2 }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            justifyContent="space-between"
          >
            <TextField
              placeholder="Search by customer, phone, or car... (Press Enter)"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyPress={handleSearchKeyPress}
              sx={{ flexGrow: 1, maxWidth: { sm: 400 } }}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            <Stack direction="row" alignItems="center" spacing={1} sx={{ flexShrink: 0 }}>
              <Button
                onClick={() => setShowFilters(!showFilters)}
                variant={hasActiveFilters ? 'contained' : 'outlined'}
                startIcon={<FilterListIcon />}
                endIcon={showFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                size="small"
              >
                Filters {hasActiveFilters && `(${activeFiltersCount})`}
              </Button>
              {hasActiveFilters && (
                <Button onClick={clearFilters} variant="text" size="small" startIcon={<ClearIcon />}>
                  Clear all
                </Button>
              )}
            </Stack>
          </Stack>

          <Collapse in={showFilters}>
            <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={statusFilter}
                      onChange={(e) => handleStatusFilterChange(e.target.value)}
                      label="Status"
                    >
                      <MenuItem value="all">All Statuses</MenuItem>
                      {RESERVATION_STATUSES.map((s) => (
                        <MenuItem key={s} value={s}>
                          {s}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={9}>
                  <Box sx={{ display: 'flex', alignItems: 'center', height: '40px' }}>
                    <Typography variant="body2" color="text.secondary">
                      {hasActiveFilters
                        ? `Found ${totalFiltered} reservations matching your filters`
                        : `Showing ${totalFiltered} reservations`}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Collapse>
        </Paper>
      </Box>

      {/* ── Scrollable Body ── */}
      <Box
        sx={{
          flex: 1,
          overflow: { xs: 'visible', md: 'auto' },
          display: 'flex',
          flexDirection: 'column',
          minHeight: { xs: 'auto', md: 0 },
        }}
      >
        {/* ── Empty state ── */}
        {pageItems.length === 0 && !loading && (
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
            <Card sx={{ width: '100%', maxWidth: 500 }}>
              <CardContent sx={{ textAlign: 'center', py: 6 }}>
                <ReceiptLongIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
                <Typography variant="h5" color="text.secondary" gutterBottom>
                  {hasActiveFilters ? 'No Reservations Match Your Filters' : 'No Reservations Yet'}
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 400, mx: 'auto' }}>
                  {hasActiveFilters
                    ? 'Try adjusting your search or filter criteria.'
                    : 'Enquiries and reservations submitted from the website will appear here.'}
                </Typography>
                {hasActiveFilters && (
                  <Button onClick={clearFilters} variant="outlined" startIcon={<ClearIcon />}>
                    Clear Filters
                  </Button>
                )}
              </CardContent>
            </Card>
          </Box>
        )}

        {/* ── Table + Pagination ── */}
        {pageItems.length > 0 && (
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: { xs: 'auto', md: 0 } }}>
            <Box
              ref={tableWrapperRef}
              sx={{ flex: 1, overflow: 'auto', mb: 2, minHeight: { xs: 'auto', md: 0 } }}
            >
              <ReservationTable
                reservations={pageItems}
                onDelete={setDeleteTarget}
                onApprove={openApprove}
                onReject={openReject}
                deleteLoading={deleteLoading}
                isDark={isDark}
                canEdit={canEdit}
                canDelete={canDelete}
              />
            </Box>

            <Paper elevation={2} sx={{ flexShrink: 0, borderRadius: 1 }}>
              <Box sx={{ py: 2, px: 2 }}>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={{ xs: 1.5, sm: 3 }}
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ width: '100%' }}
                >
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                    Showing {totalFiltered === 0 ? 0 : (currentPage - 1) * RESERVATIONS_PER_PAGE + 1}–
                    {Math.min(currentPage * RESERVATIONS_PER_PAGE, totalFiltered)} of {totalFiltered} reservations
                  </Typography>
                  <Pagination
                    count={Math.max(1, totalPages)}
                    page={currentPage}
                    onChange={handlePageChange}
                    color="primary"
                    size={isMobile ? 'small' : 'medium'}
                    showFirstButton
                    showLastButton
                    disabled={totalPages <= 1}
                  />
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                    Page {currentPage} of {Math.max(1, totalPages)}
                  </Typography>
                </Stack>
              </Box>
            </Paper>
          </Box>
        )}
      </Box>

      {/* ── Approve/Reject Drawer ── */}
      <ReservationDecisionDrawer
        open={!!decisionTarget}
        reservation={decisionTarget}
        decision={decisionType}
        onClose={closeDecisionDialog}
        onConfirm={handleDecisionConfirm}
      />

      {/* ── Add Reservation Drawer ── */}
      <AddReservationDrawer
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        onSubmit={handleAddReservation}
      />

      {/* ── Delete Confirmation Dialog ── */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Reservation</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Delete this reservation/enquiry from <strong>"{deleteTarget?.customerName}"</strong>? This action cannot
            be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleteDialogLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteConfirm}
            disabled={deleteDialogLoading}
            startIcon={deleteDialogLoading ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            {deleteDialogLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Toast ── */}
      <ToastNotification open={toast.open} message={toast.message} severity={toast.severity} onClose={handleCloseToast} />
    </Box>
  );
};

export default ReservationsManagement;
